<template>
    <div>
        <rkm-alert
            v-if="whatsappEnabled"
            color="info"
            class="mb-3"
        >
            Solicite ao paciente o consentimento para receber pelo WhatsApp mensagens relacionadas
            aos serviços de saúde, como agendamentos, confirmações e lembretes.
        </rkm-alert>

        <rkm-form
            v-if="!readonly"
            ref="Form"
        >
            <rkm-row class="flex-nowrap">
                <rkm-col cols="3">
                    <rkm-form-input
                        label="Número"
                        v-model="telefone.numero"
                        v-mask="numeroMask"
                        :validations="validations.numero"
                    />
                </rkm-col>

                <rkm-col class="flex-grow-1">
                    <rkm-form-input
                        label="Observação"
                        v-model="telefone.observacao"
                        :validations="validations.observacao"
                        uppercase
                    />
                </rkm-col>

                <rkm-col class="flex-grow-0">
                    <rkm-button
                        :label="false"
                        color="primary"
                        @click.stop="$refs.Form.validate(add)"
                    >
                        {{ editingIndex === null ? 'Incluir' : 'Atualizar' }}
                    </rkm-button>

                    <rkm-button
                        v-if="editingIndex !== null"
                        :label="false"
                        class="ml-2"
                        @click.stop="resetForm"
                    >
                        Cancelar
                    </rkm-button>
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <rkm-table
            :fields="fields"
            :items="telefones"
            ref="Table"
            hide-filters
        >
            <template v-slot:cell(principal)="row">
                <rkm-form-radio
                    v-model="row.item.principal"
                    name="principal"
                    :value="true"
                    :disabled="readonly || (hasCellPhone && isFixo(row.item))"
                    inline
                    @click.stop="selectPrincipal(row)"
                />
            </template>

            <template v-slot:cell(autorizaSms)="row">
                <template v-if="isFixo(row.item)">
                    <rkm-form-radio
                        disabled
                        inline
                    />
                </template>

                <template v-else>
                    <rkm-form-radio
                        v-model="row.item.autorizaSms"
                        name="autorizaSms"
                        :value="true"
                        :disabled="readonly"
                        inline
                        @click.stop="selectAutorizaSms(row)"
                    />
                </template>
            </template>

            <template v-slot:cell(whatsapp)="row">
                <template v-if="!isFixo(row.item)">
                    <rkm-button
                        :disabled="!canManifest(row.item)"
                        :title="whatsappButtonTitle()"
                        small
                        @click.stop="openWhatsappManifestation(row.index)"
                    >
                        WhatsApp?
                    </rkm-button>
                    <span class="ml-2">{{ whatsappManifestationLabel(row.item) }}</span>
                </template>
            </template>

            <template v-slot:cell(actions)="{ item, index }">
                <rkm-button
                    color="primary"
                    title="Editar"
                    small
                    class="mr-1"
                    @click.stop="edit(item, index)"
                >
                    <rkm-icon icon="pen" />
                </rkm-button>
                <rkm-button
                    color="danger"
                    title="Excluir"
                    small
                    @click.stop="handleDestroy(index)"
                >
                    <rkm-icon icon="trash" />
                </rkm-button>
            </template>
        </rkm-table>

        <rkm-modal
            :buttons="[]"
            ref="WhatsappModal"
            title="Manifestação para WhatsApp"
        >
            Autoriza receber mensagens de serviços de saúde pelo WhatsApp?

            <template v-slot:footer>
                <rkm-button @click="$refs.WhatsappModal.hide()">
                    Cancelar
                </rkm-button>
                <rkm-button color="danger" @click="setWhatsappManifestation('negado')">
                    Não
                </rkm-button>
                <rkm-button
                    color="primary"
                    :disabled="!canAuthorizeSelectedPhone"
                    @click="setWhatsappManifestation('autorizado')"
                >
                    Sim
                </rkm-button>
            </template>
        </rkm-modal>
    </div>
</template>

<script>
// Mixins.
import { mask, object, unmask } from '@/mixins'

export default {
    mixins: [ mask, object, unmask ],
    props: {
        readonly: { type: Boolean, default: false },
        telefones: { type: Array, required: true },
        whatsappEnabled: { type: Boolean, default: false },
    },
    data: () => ({
        editingIndex: null,
        indexCheckedAutorizaSms: null,
        originalPhoneNumbers: new WeakMap(),
        whatsappManifestationIndex: null,
        telefone: {
            id: null,
            numero: '',
            observacao: '',
            autorizaSms: false,
            principal: false,
            whatsappManifestacao: null,
        },
    }),
    computed: {
        validations() {

            return {
                numero: {
                    required: true,
                    min_max_without_mask: [10, 11]
                },
                observacao: {
                    max: 50
                }
            }
        },
        numeroMask() {

            if (this.telefone.numero === '') {

                return ''
            }

            return this.telefone.numero.length < 15 ? this.mask.phone : this.mask.cellPhone
        },
        canAuthorizeSelectedPhone() {

            if (this.whatsappManifestationIndex === null) {

                return false
            }

            const telefone = this.telefones[this.whatsappManifestationIndex]

            return telefone ? !this.isFixo(telefone) : false
        },
        hasCellPhone() {

            return this.telefones.some(telefone => this.unMaskPhone(telefone.numero).length === 11)
        },
        fields() {

            const fields = [
                {
                    key: 'principal',
                    label: 'Principal',
                    class: 'col-min text-center'
                },
                {
                    key: 'autorizaSms',
                    label: 'SMS',
                    headerTitle: 'Autoriza recebimento de SMS',
                    class: 'col-min text-center'
                },
                { key: 'numero', label: 'Número', sortable: true },
                { key: 'observacao', label: 'Observação', sortable: true },
            ]

            if (!this.readonly) {

                fields.push({ key: 'actions', label: 'Ações', class: 'col-min' })
            }

            if (this.whatsappEnabled) {

                fields.splice(2, 0, {
                    key: 'whatsapp',
                    label: 'WhatsApp',
                    class: 'col-min text-nowrap'
                })
            }

            return fields
        }
    },
    methods: {
        async validate() {

            if (this.telefones.length === 0) {

                this.showValidationNotification('Obrigatório incluir pelo menos um telefone.')

                return false
            }
            else if (this.telefones.filter(t => t.principal === true).length !== 1) {

                this.showValidationNotification('Obrigatório marcar exatamente um telefone como principal.')

                return false
            }

            else {

                const principal = this.telefones.find(t => t.principal === true)

                if (this.hasCellPhone && this.isFixo(principal)) {

                    this.showValidationNotification(
                        'Quando houver celular cadastrado, o telefone principal deve ser um celular.'
                    )

                    return false
                }

                if (this.whatsappEnabled
                    && !this.isFixo(principal)
                    && !['autorizado', 'negado'].includes(principal.whatsappManifestacao)) {

                    this.showValidationNotification('Informe se o paciente autoriza mensagens pelo WhatsApp.')

                    return false
                }
            }

            return true
        },
        add() {

            const telefone = this.clone(this.telefone)

            if (this.editingIndex !== null) {

                const original = this.telefones[this.editingIndex]

                if (this.unMaskPhone(original.numero) !== this.unMaskPhone(telefone.numero)) {

                    telefone.whatsappManifestacao = this.defaultWhatsappManifestation()
                }

                this.$set(this.telefones, this.editingIndex, telefone)
                this.resetForm()

                return
            }

            telefone.whatsappManifestacao = this.defaultWhatsappManifestation()

            if (this.telefones.length === 0) {

                telefone.principal = true
            }

            this.telefones.push(telefone)

            this.resetForm()
        },
        resetForm() {

            this.telefone.id = null
            this.telefone.numero = ''
            this.telefone.observacao = ''
            this.telefone.autorizaSms = false
            this.telefone.principal = false
            this.telefone.whatsappManifestacao = null
            this.editingIndex = null

            this.$nextTick(() => this.$refs.Form.resetValidator())
        },
        handleDestroy(index) {

            const destroy = async () => {

                this.telefones.splice(index, 1)

                if (this.editingIndex === index) {

                    this.resetForm()
                }

                if (this.telefones.length === 1) {

                    this.telefones[0].principal = true
                }
            }

            this.showDeleteConfirmation('Deseja realmente excluir o telefone?', destroy)
        },
        isFixo({ numero }) {

            return this.unMaskPhone(numero).length === 10
        },
        defaultWhatsappManifestation() {
            return null
        },
        edit(item, index) {

            this.telefone = this.clone(item)
            this.editingIndex = index
        },
        selectPrincipal({ index }) {

            const telefone = this.telefones[index]

            if (!this.readonly && telefone && !(this.hasCellPhone && this.isFixo(telefone))) {

                this.telefones.map((_, i) => i !== index && (this.telefones[i].principal = false))
                this.telefones.map((_, i) => i == index && (this.telefones[i].principal = true))
            }
        },
        canManifest(telefone) {

            return this.whatsappEnabled
                && !this.readonly
                && !this.isFixo(telefone)
        },
        whatsappButtonTitle() {
            return 'Registrar manifestação do paciente.'
        },
        whatsappManifestationLabel(telefone) {

            return {
                autorizado: 'Autorizado',
                negado: 'Não autorizado',
            }[telefone.whatsappManifestacao] || 'Não respondido'
        },
        openWhatsappManifestation(index) {

            this.whatsappManifestationIndex = index
            this.$refs.WhatsappModal.show()
        },
        setWhatsappManifestation(manifestation) {

            this.$set(this.telefones[this.whatsappManifestationIndex], 'whatsappManifestacao', manifestation)
            this.$refs.WhatsappModal.hide()
        },
        selectAutorizaSms({ index }) {

            if (!this.readonly) {

                this.telefones.map((_, i) => i !== index && (this.telefones[i].autorizaSms = false))


                if (this.indexCheckedAutorizaSms !== null && this.indexCheckedAutorizaSms === index) {

                    this.indexCheckedAutorizaSms = null
                    this.telefones[index].autorizaSms = false
                }
                else {

                    this.indexCheckedAutorizaSms = index
                }

                this.telefones.map((_, i) => i == index && (this.telefones[i].autorizaSms = true))
            }
        },
    },
    watch: {
        telefones: {
            deep: true,
            immediate: true,
            handler(telefones) {

                telefones.forEach(telefone => {

                    const numero = this.unMaskPhone(telefone.numero)

                    if (this.originalPhoneNumbers.has(telefone)
                        && this.originalPhoneNumbers.get(telefone) !== numero) {

                        this.$set(
                            telefone,
                            'whatsappManifestacao',
                            this.defaultWhatsappManifestation()
                        )
                    }

                    this.originalPhoneNumbers.set(telefone, numero)
                })
            },
        },
    },
}
</script>
